import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import DeleteIcon from '@mui/icons-material/DeleteSweep'
import { useLiveQuery } from 'dexie-react-hooks'
import { dbFor, wipeStore } from '../data/db.js'
import { useConflicts, useSyncStatus } from '../data/hooks.js'
import { deviceId, devicePlatform } from '../data/device.js'
import { relancerBloquees, runCycle } from '../data/sync/engine.js'
import { ConfirmDialog } from '../ui/ConfirmDialog.js'
import { useSessionStore } from '../stores/sessionStore.js'

/**
 * Paramètres › Synchronisation (W-33).
 *
 * Cet écran existe pour une raison précise : quand la synchronisation
 * dysfonctionne, l'utilisateur n'a autrement AUCUN moyen de savoir ce que son
 * appareil détient, ni d'agir. Il rend visible ce qui est sinon invisible.
 */

const PLAFOND_MO = 250

/** Taille approchée de la réplique. `navigator.storage.estimate()` mesure tout
 *  l'origine (caches compris) : on préfère peser les lignes elles-mêmes. */
function poidsMo(lignes: unknown[]): number {
  let octets = 0
  for (const l of lignes) octets += JSON.stringify(l).length
  return octets / (1024 * 1024)
}

/** Unité adaptée à la grandeur : « 0,00 Mo » quatre fois de suite n'informe pas. */
function taille(mo: number): string {
  if (mo >= 1) return `${mo.toFixed(1).replace('.', ',')} Mo`
  const ko = mo * 1024
  if (ko >= 1) return `${Math.round(ko)} Ko`
  // « — » laisserait croire qu'il n'y a rien, alors que la ligne compte des
  // éléments : on distingue « vide » de « négligeable ».
  return ko > 0 ? '< 1 Ko' : '—'
}

function ilYA(iso: string | null): string {
  if (!iso) return 'jamais'
  const secondes = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (secondes < 60) return 'il y a moins d’une minute'
  const minutes = Math.round(secondes / 60)
  if (minutes < 60) return `il y a ${minutes} min`
  const heures = Math.round(minutes / 60)
  if (heures < 24) return `il y a ${heures} h`
  return `il y a ${Math.round(heures / 24)} j`
}

export function SyncSettingsPage({
  /** Montée dans un onglet de Paramètres, qui porte déjà le titre de l'écran. */
  sansEntete = false,
}: { sansEntete?: boolean } = {}): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))
  const storeId = store?.storeId

  const sync = useSyncStatus()
  const conflicts = useConflicts(storeId)
  const [purging, setPurging] = useState(false)

  const contenu = useLiveQuery(async () => {
    if (!storeId) return null
    const db = dbFor(storeId)
    const [customers, items, categories, suppliers, outbox] = await Promise.all([
      db.customers.toArray(),
      db.items.toArray(),
      db.categories.toArray(),
      db.suppliers.toArray(),
      db.outbox.toArray(),
    ])
    return { customers, items, categories, suppliers, outbox }
  }, [storeId])

  if (!store) return <Alert severity="error">Boutique introuvable</Alert>

  const groupes = contenu
    ? [
        { nom: 'Clients', lignes: contenu.customers },
        { nom: 'Produits & Services', lignes: contenu.items },
        { nom: 'Catégories', lignes: contenu.categories },
        { nom: 'Fournisseurs', lignes: contenu.suppliers },
      ]
    : []

  const totalMo = groupes.reduce((somme, g) => somme + poidsMo(g.lignes), 0)
  const enAttente = contenu?.outbox.filter((e) => e.status !== 'conflict').length ?? 0
  const refusees = contenu?.outbox.filter((e) => e.lastError && e.status === 'failed') ?? []

  return (
    <Box>
      {!sansEntete && (
        <>
          <Typography variant="h1" sx={{ mb: 1 }}>
            Synchronisation
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5 }}>
            Ce que cet appareil détient hors ligne, et ce qu'il reste à envoyer.
          </Typography>
        </>
      )}

      <Paper sx={{ p: 6, borderRadius: 3, mb: 4 }}>
        <Typography variant="h3" sx={{ mb: 4 }}>
          État de cet appareil
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', mb: 4 }}>
          <Chip
            size="small"
            color={
              conflicts.length > 0
                ? 'error'
                : sync.phase === 'offline' || sync.phase === 'error'
                  ? 'warning'
                  : 'success'
            }
            label={
              conflicts.length > 0
                ? `${conflicts.length} conflit${conflicts.length > 1 ? 's' : ''}`
                : sync.phase === 'offline' || sync.phase === 'error'
                  ? 'Hors ligne'
                  : sync.phase === 'idle'
                    ? 'Synchronisé'
                    : 'Synchronisation…'
            }
          />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Dernière synchronisation {ilYA(sync.lastSyncAt)}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            disabled={sync.phase === 'pulling' || sync.phase === 'pushing'}
            onClick={() => void runCycle(store.storeId)}
          >
            Synchroniser maintenant
          </Button>
        </Box>

        {sync.phase === 'bootstrapping' && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Chargement initial — {Math.round(sync.progress * 100)} %
            </Typography>
            <LinearProgress variant="determinate" value={sync.progress * 100} sx={{ mt: 1 }} />
          </Box>
        )}

        <Ligne label="Modifications en attente d'envoi" valeur={String(enAttente)} />
        <Ligne label="Conflits à arbitrer" valeur={String(conflicts.length)} />
        <Ligne label="Appareil" valeur={`${devicePlatform()} · ${deviceId().slice(0, 8)}`} />

        {sync.error && (
          <Alert severity="warning" sx={{ mt: 4 }}>
            Dernier échec : {sync.error}
          </Alert>
        )}

        {refusees.length > 0 && (
          <Alert
            severity="error"
            sx={{ mt: 4 }}
            /* ⚠️ Sans ce bouton, une mutation refusée est PERDUE : elle est
               parquée pour toujours et rien, dans l'interface, ne permettait de
               la rejouer. Or un refus venu du serveur peut cesser d'être vrai
               après correction — c'est arrivé avec le plafond de taille des
               visuels. La saisie de l'utilisateur ne doit pas dépendre de cela. */
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  if (storeId) void relancerBloquees(storeId)
                }}
              >
                Réessayer
              </Button>
            }
          >
            {refusees.length} modification{refusees.length > 1 ? 's' : ''} refusée
            {refusees.length > 1 ? 's' : ''} par le serveur :{' '}
            {refusees[0]?.lastError}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 6, borderRadius: 3, mb: 4 }}>
        <Typography variant="h3" sx={{ mb: 4 }}>
          Données disponibles hors ligne
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Entité</TableCell>
              <TableCell align="right">Éléments</TableCell>
              <TableCell align="right">Taille</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupes.map((g) => (
              <TableRow key={g.nom}>
                <TableCell>{g.nom}</TableCell>
                <TableCell align="right" className="tabular">
                  {g.lignes.length}
                </TableCell>
                <TableCell align="right" className="tabular">
                  {taille(poidsMo(g.lignes))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ mt: 4 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Espace utilisé : {taille(totalMo)} sur {PLAFOND_MO} Mo
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min((totalMo / PLAFOND_MO) * 100, 100)}
            sx={{ mt: 1 }}
          />
        </Box>

        <Alert severity="info" variant="outlined" sx={{ mt: 4 }}>
          Transactions, rendez-vous et stock rejoindront cette réplique à mesure que leurs
          modules arriveront.
        </Alert>
      </Paper>

      <Paper sx={{ p: 6, borderRadius: 3 }}>
        <Typography variant="h3" sx={{ mb: 3 }}>
          Vider les données locales
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
          Supprime la réplique de cette boutique sur cet appareil. Elle sera rechargée depuis le
          serveur à la prochaine ouverture.
        </Typography>
        <Button
          color="error"
          variant="outlined"
          startIcon={<DeleteIcon />}
          onClick={() => setPurging(true)}
        >
          Vider les données locales
        </Button>
      </Paper>

      <ConfirmDialog
        open={purging}
        title="Vider la réplique locale ?"
        message={
          enAttente > 0
            ? `⚠ ${enAttente} modification${enAttente > 1 ? 's' : ''} n'${enAttente > 1 ? 'ont' : 'a'} pas encore été envoyée${enAttente > 1 ? 's' : ''} au serveur. Elle${enAttente > 1 ? 's' : ''} sera${enAttente > 1 ? 'ont' : ''} définitivement perdue${enAttente > 1 ? 's' : ''}.`
            : 'Toutes vos modifications ont été envoyées. La réplique sera rechargée depuis le serveur.'
        }
        confirmLabel="Vider"
        onConfirm={async () => {
          await wipeStore(store.storeId)
          // On quitte la page : ses requêtes réactives pointent sur une base
          // qui vient d'être détruite.
          navigate(`/s/${slug}/dashboard`)
        }}
        onClose={() => setPurging(false)}
      />
    </Box>
  )
}

function Ligne({ label, valeur }: { label: string; valeur: string }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', py: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" className="tabular">
        {valeur}
      </Typography>
    </Box>
  )
}
